import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    icon: {
      borderRadius: "32px",
      padding: theme.spacing(2, 6),
      fontSize: "12px",
      lineHeight: "16px",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(4),
    },
    succeed: {
      backgroundColor: theme.green50,
      color: theme.green800,
    },
    error: {
      backgroundColor: theme.red50,
      color: theme.red800,
    },
    neutral: {
      backgroundColor: theme.primary50,
      color: theme.primary800,
      "& svg": {
        color: theme.primary500,
        height: "16px",
        width: "16px",
      },
    },
    warning: {
      backgroundColor: theme.orange50,
      color: theme.orange800,
    },
  }),
);

interface StatusValueProps {
  status?: boolean | "warning" | "neutral";
  label?: string | JSX.Element;
  "data-test-id"?: string;
}

export const StatusValue = ({
  status,
  "data-test-id": dataTestId,
  label,
}: StatusValueProps) => {
  const classes = useStyles();
  const { message } = useMessage();

  const getDetails = () => {
    switch (status) {
      // Positive
      case true:
        return {
          className: classes.succeed,
          name: label || message({ id: "active" }),
        };
      // Negative
      case false:
        return {
          className: classes.error,
          name: label || message({ id: "inactive" }),
        };
      // Warning
      case "warning":
        return { className: classes.warning, name: label || "" };
      // Neutral
      default:
        return { className: classes.neutral, name: label || "" };
    }
  };

  const { className, name } = getDetails();

  return (
    <div
      className={`${classes.icon} ${className}`}
      data-test-id={dataTestId && `${dataTestId}-${status || "neutral"}-status`}
    >
      {name}
    </div>
  );
};
